import { useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import {
  SidebarDivider,
  useSidebarOpenState,
} from '@backstage/core-components';
import { useApi, storageApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { CustomSidebarItem } from './CustomSidebarItem';
import { SIDEBAR_ITEMS_CONFIG, STORAGE_BUCKET, FAVORITES_KEY } from './sidebarConstants';
import { useSidebarPreferences } from './SidebarPreferencesContext';
import { useFeatureFlag } from '@internal/plugin-feature-flags-common/components';
import {
  ADR_FEATURE_FLAG,
  GOLDEN_PATHS_FEATURE_FLAG,
  MY_AI_TOOLS_FEATURE_FLAG,
} from '@internal/plugin-feature-flags-common';

const useStyles = makeStyles(theme => ({
  labelExpanded: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 2.5),
    gap: theme.spacing(0.5),
    color: theme.palette.warning.main,
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    opacity: 0.9,
  },
  labelCollapsed: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 0),
    color: theme.palette.warning.main,
  },
  starIcon: {
    fontSize: '0.85rem',
  },
  draggableItem: {
    position: 'relative',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragging: {
    opacity: 0.5,
    background: theme.palette.action.hover,
  },
  dragOver: {
    borderTop: `2px solid ${theme.palette.primary.main}`,
  },
  dragHandle: {
    position: 'absolute',
    left: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.text.secondary,
    opacity: 0,
    transition: 'opacity 0.2s ease',
    cursor: 'grab',
    zIndex: 1,
    '&:active': {
      cursor: 'grabbing',
    },
  },
  draggableWrapper: {
    position: 'relative',
    '&:hover $dragHandle': {
      opacity: 0.6,
    },
  },
}));

export const FavoritesSidebarSection = () => {
  const classes = useStyles();
  const alertApi = useApi(alertApiRef);
  const { favorites } = useSidebarPreferences();
  const baseStorageApi = useApi(storageApiRef);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  const { isOpen } = useSidebarOpenState();
  const { enabled: isAdrEnabled } = useFeatureFlag(ADR_FEATURE_FLAG);
  const { enabled: isGoldenPathsEnabled } = useFeatureFlag(
    GOLDEN_PATHS_FEATURE_FLAG,
  );
  const { enabled: isMyAiToolsEnabled } = useFeatureFlag(
    MY_AI_TOOLS_FEATURE_FLAG,
  );

  const visibleFavorites = favorites.filter(route => {
    if (route === '/golden-paths' && !isGoldenPathsEnabled) {
      return false;
    }
    if (route === '/adr' && !isAdrEnabled) {
      return false;
    }
    if (route === '/my-ai-tools' && !isMyAiToolsEnabled) {
      return false;
    }
    return Boolean(SIDEBAR_ITEMS_CONFIG[route]);
  });

  // Drag and drop handlers
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, route: string) => {
      setDraggedItem(route);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', route);
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverItem(null);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, route: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (draggedItem && draggedItem !== route) {
        setDragOverItem(route);
      }
    },
    [draggedItem],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverItem(null);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>, targetRoute: string) => {
      e.preventDefault();
      setDragOverItem(null);

      if (!draggedItem || draggedItem === targetRoute) {
        return;
      }

      // Reorder favorites
      const newFavorites = [...favorites];
      const draggedIndex = newFavorites.indexOf(draggedItem);
      const targetIndex = newFavorites.indexOf(targetRoute);

      if (draggedIndex === -1 || targetIndex === -1) {
        return;
      }

      // Remove dragged item and insert at new position
      newFavorites.splice(draggedIndex, 1);
      newFavorites.splice(targetIndex, 0, draggedItem);

      // Save new order to storage
      try {
        // Use the storage API directly for reordering since we need to set the full array
        const storageApi = baseStorageApi.forBucket(STORAGE_BUCKET);
        await storageApi.set(FAVORITES_KEY, newFavorites);
        setDraggedItem(null);
      } catch (error) {
        alertApi.post({
          message: 'Failed to reorder favorites. Please try again.',
          severity: 'error',
          display: 'transient',
        });
        setDraggedItem(null);
      }
    },
    [draggedItem, favorites, baseStorageApi, alertApi],
  );

  // Don't render section if no favorites
  if (visibleFavorites.length === 0) {
    return null;
  }

  return (
    <>
      {/* Compact Favorites Label */}
      <div
        className={isOpen ? classes.labelExpanded : classes.labelCollapsed}
        role="heading"
        aria-level={2}
        aria-label="Favorites section"
      >
        <StarIcon className={classes.starIcon} aria-hidden="true" />
        {isOpen && <span>Favorites</span>}
      </div>
      {/* Favorite Items */}
      {visibleFavorites.map((route, index) => {
        const config = SIDEBAR_ITEMS_CONFIG[route];
        if (!config) return null;
        return (
          <div
            key={route}
            className={`${classes.draggableWrapper} ${
              draggedItem === route ? classes.dragging : ''
            } ${dragOverItem === route ? classes.dragOver : ''}`}
            draggable
            onDragStart={e => handleDragStart(e, route)}
            onDragEnd={handleDragEnd}
            onDragOver={e => handleDragOver(e, route)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, route)}
            role="group"
            aria-label={`${config.text} - favorite item ${index + 1} of ${visibleFavorites.length}. Drag to reorder.`}
          >
            {isOpen && (
              <DragIndicatorIcon
                className={classes.dragHandle}
                style={{ fontSize: 16 }}
                aria-hidden="true"
              />
            )}
            <CustomSidebarItem
              icon={config.icon}
              to={route}
              text={config.text}
            />
          </div>
        );
      })}
      {/* Divider to separate from other sections */}
      <SidebarDivider />
    </>
  );
};

export default FavoritesSidebarSection;
